/** Visual tone for operation status in list + receipt. */
export type StatusTone =
  | "initial"
  | "progress"
  | "completed"
  | "failed"
  | "expired";

export type StatusPresentation = {
  tone: StatusTone;
  labelKey: string;
};

/** Hex colors for status tones (list text+icon, receipt text). */
export const STATUS_TONE_COLOR: Record<StatusTone, string> = {
  initial: "#F28D19",
  progress: "#252525",
  completed: "#31C859",
  failed: "#FF3B30",
  expired: "#8a8a8a",
};

/**
 * Maps a backend operation status to tone + i18n key under `operation.status.*`.
 * Cancelled shares the failed (red) tone but keeps a distinct label.
 */
export const toStatusPresentation = (
  status: string | undefined
): StatusPresentation => {
  switch ((status ?? "").toLowerCase()) {
    case "created":
    case "pending":
      return { tone: "initial", labelKey: "operation.status.initial" };
    case "in_review":
      return { tone: "initial", labelKey: "operation.status.inReview" };
    case "completed":
    case "funded":
    case "paid":
      return { tone: "completed", labelKey: "operation.status.completed" };
    case "failed":
    case "rejected":
      return { tone: "failed", labelKey: "operation.status.failed" };
    case "cancelled":
    case "canceled":
      return { tone: "failed", labelKey: "operation.status.cancelled" };
    case "expired":
      return { tone: "expired", labelKey: "operation.status.expired" };
    default:
      return { tone: "progress", labelKey: "operation.status.processing" };
  }
};

/** @deprecated Prefer `toStatusPresentation(status).labelKey`. */
export const operationStatusKey = (status: string | undefined): string =>
  toStatusPresentation(status).labelKey;

export const operationStatusTone = (status: string | undefined): StatusTone =>
  toStatusPresentation(status).tone;

/** True when the operation reached a successful terminal state. */
export const isOperationComplete = (status: string | undefined): boolean =>
  operationStatusTone(status) === "completed";

/** True when the operation failed (terminal error, not cancelled/expired). */
export const isOperationFailed = (status: string | undefined): boolean => {
  const s = (status ?? "").toLowerCase();
  return s === "failed" || s === "rejected";
};

/** True when the operation reached any terminal state. */
export const isOperationTerminal = (status: string | undefined): boolean => {
  const tone = operationStatusTone(status);
  return tone === "completed" || tone === "failed" || tone === "expired";
};

/** i18n key for the receipt hero line based on operation status. */
export const receiptHeroKey = (status: string | undefined): string => {
  const s = (status ?? "").toLowerCase();
  if (isOperationFailed(s)) return "receipt.transferFailed";
  if (s === "cancelled" || s === "canceled") return "receipt.statusCancelled";
  if (s === "expired") return "receipt.statusExpired";
  return "receipt.youSent";
};
